import { useEffect, useMemo, useState } from "react";
import { Loader2, Settings2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  type FilterFieldOption,
  listAllFilterFieldOptions,
} from "@/components/ProjectChatFilters";
import { useProjectCustomFields } from "@/hooks/useProjectCustomFields";
import { useUpdateProject } from "@/hooks/useProjects";

interface ProjectChatFiltersConfigDialogProps {
  projectId: string;
  projectName: string;
  // null = no override saved yet → all options visible
  visibleKeys: string[] | null;
}

const ProjectChatFiltersConfigDialog = ({
  projectId,
  projectName,
  visibleKeys,
}: ProjectChatFiltersConfigDialogProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          title="Configure chat filters"
        >
          <Settings2 className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      {open && (
        <ProjectChatFiltersConfigDialogContent
          projectId={projectId}
          projectName={projectName}
          visibleKeys={visibleKeys}
          onClose={() => setOpen(false)}
        />
      )}
    </Dialog>
  );
};

interface ContentProps extends ProjectChatFiltersConfigDialogProps {
  onClose: () => void;
}

const ProjectChatFiltersConfigDialogContent = ({
  projectId,
  projectName,
  visibleKeys,
  onClose,
}: ContentProps) => {
  const { data: customFieldDefs = [], isLoading } =
    useProjectCustomFields(projectId);
  const updateProject = useUpdateProject();

  const options = useMemo(
    () => listAllFilterFieldOptions(customFieldDefs),
    [customFieldDefs],
  );

  // Local working set keyed by option.key. Initialise from the saved value
  // when present; otherwise treat "no override" as "all visible" — matches
  // the runtime default and lets admins see what's currently in effect.
  const [selected, setSelected] = useState<Set<string>>(() =>
    visibleKeys === null
      ? new Set(options.map((o) => o.key))
      : new Set(visibleKeys),
  );

  // If custom fields load after the initial render, pull any new keys in
  // when the working set is currently "all" (so admins don't have to
  // re-tick newly-added custom fields). When the admin has a specific
  // subset selected, leave it alone.
  useEffect(() => {
    if (visibleKeys !== null) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const o of options) next.add(o.key);
      return next;
    });
  }, [options, visibleKeys]);

  const toggle = (key: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const setAll = (checked: boolean) => {
    setSelected(checked ? new Set(options.map((o) => o.key)) : new Set());
  };

  const groupedOptions = useMemo(() => {
    const standard: FilterFieldOption[] = [];
    const custom: FilterFieldOption[] = [];
    for (const o of options) {
      (o.group === "standard" ? standard : custom).push(o);
    }
    return { standard, custom };
  }, [options]);

  const handleSave = async () => {
    // Persist `null` when every option is ticked so the project keeps the
    // "show all (including future fields)" default — saving an explicit
    // array would lock the visible set as more custom fields are added.
    const everySelected =
      options.length > 0 && options.every((o) => selected.has(o.key));
    const payload = everySelected ? null : Array.from(selected);
    try {
      await updateProject.mutateAsync({
        id: projectId,
        visibleChatFilterKeys: payload,
      });
      toast.success("Chat filters updated.");
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save chat filters.";
      toast.error(message);
    }
  };

  const totalCount = options.length;
  const selectedCount = options.filter((o) => selected.has(o.key)).length;
  const allChecked = totalCount > 0 && selectedCount === totalCount;
  const someChecked = selectedCount > 0 && selectedCount < totalCount;

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Chat filters — {projectName}</DialogTitle>
        <DialogDescription>
          Pick which metadata fields appear in the chat filter panel for
          this project. Unticked fields stay hidden from everyone, even
          when documents have values for them.
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading fields…
        </div>
      ) : totalCount === 0 ? (
        <div className="py-4 text-sm text-muted-foreground">
          No filter fields available for this project.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {selectedCount} of {totalCount} visible
            </span>
            <button
              type="button"
              onClick={() => setAll(!allChecked)}
              className="font-medium text-brand-teal hover:underline"
            >
              {allChecked ? "Clear all" : "Select all"}
            </button>
          </div>
          <ScrollArea className="max-h-72 [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="space-y-3 pr-2">
              <FieldGroup
                heading="Standard fields"
                options={groupedOptions.standard}
                selected={selected}
                onToggle={toggle}
              />
              {groupedOptions.custom.length > 0 && (
                <>
                  <Separator />
                  <FieldGroup
                    heading="Project custom fields"
                    options={groupedOptions.custom}
                    selected={selected}
                    onToggle={toggle}
                  />
                </>
              )}
            </div>
          </ScrollArea>
          {someChecked && selectedCount === 0 && (
            <div className="text-xs text-destructive">
              At least one filter must remain visible — otherwise the chat
              panel will be empty.
            </div>
          )}
        </>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={updateProject.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={updateProject.isPending || isLoading}
          className="bg-brand-teal text-white hover:bg-brand-teal/90"
        >
          {updateProject.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

interface FieldGroupProps {
  heading: string;
  options: FilterFieldOption[];
  selected: Set<string>;
  onToggle: (key: string, checked: boolean) => void;
}

const FieldGroup = ({ heading, options, selected, onToggle }: FieldGroupProps) => (
  <div className="space-y-1">
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {heading}
    </div>
    <ul className="space-y-1">
      {options.map((o) => {
        const checked = selected.has(o.key);
        return (
          <li key={o.key}>
            <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent/40">
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => onToggle(o.key, v === true)}
              />
              <span className="flex-1 truncate" title={o.label}>
                {o.label}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  </div>
);

export default ProjectChatFiltersConfigDialog;
